import React from "react";
import { message, notification } from "antd";
import { observable, action, flow, toJS } from "mobx";
import {
	getLinks,
	deleteLinks,
	addLinks,
	saveLink,
	saveLinkSetKeywords
} from "services/linkSet";
import { registerHandler, delay } from "services/api";
import { createFormObject, getRawObjectFromFormObject } from "utils/utils";
import mergeWith from "lodash/mergeWith";

const pType = [
	"primaryPercentage",
	"secondaryPercentage",
	"brandedPercentage",	
	"genericPercentage",
	"nakedUrlPercentage"
];

let pTypeMap = [];
for (let i = 0; i < pType.length; i++) {
	let type = pType[i];
	pTypeMap[type] = i;
}

class LinksStore {
	@observable urls = [];
	@observable urlList = {};
	@observable urlListForm = {};
	@observable ui = {};
	@observable campaign = {};

	constructor() {
		this.ui = this.initUI();
	}

	initUI = () => {
		const ui = {
			loadingLinks: false,
			deletingLinks: false,
			newLink: null,
			updatedLink: null,
			deletedLinkIds: [],
			importedLinks: [],
			currentLink: null,
			addingLink: false,
			editingLink: false,
			addEditLinkDialogVisible: false,
			currentLinkBatch: null,
			batchLinksDialogVisible: false,
			addingLinks: false,
			savingLinkSetKeywords: false
		};

		return observable.object(ui);
	};

	remoteDeleteLinks = flow(function*(linkIds) {
		this.ui.deletingLinks = true;

		const response = yield deleteLinks(this.urlList.id, linkIds);

		this.ui.deletingLinks = false;

		if (response == null) {
			message.error("Failed to delete the links", 5);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully deleted the links", 3);
	});

	@action
	deleteLinks = deletedLinkIds => {
		this.ui.deletedLinkIds = observable.array(deletedLinkIds);
	};

	@action
	addLink = link => {
		this.ui.newLink = observable.object(link);
	};

	@action
	updateLink = updatedLink => {
		this.ui.updatedLink = observable.object(updatedLink);
	};

	@action
	importLinks = importedLinks => {
		this.ui.importedLinks = observable.array(importedLinks);
	};

	@action
	showAddLinkDialog = () => {
		this.ui.currentLink = null;
		this.ui.addEditLinkDialogVisible = true;
	};

	@action
	showBatchLinksDialog = () => {
		this.ui.currentLinkBatch = null;
		this.ui.batchLinksDialogVisible = true;
	};

	@action
	hideBatchLinksDialog = () => {
		this.ui.currentLinkBatch = null;
		this.ui.batchLinksDialogVisible = false;
	};

	@action
	showEditLinkDialog = link => {
		this.ui.currentLink = observable.object(toJS(link));
		this.ui.addEditLinkDialogVisible = true;
	};

	@action
	hideAddEditLinkDialog = () => {
		this.ui.currentLink = null;
		this.ui.addEditLinkDialogVisible = false;
	};

	@action
	keywordsFormChanged = fields => {		
		let customizer = (objValue, srcValue, key, object, source, stack) => {
			if (srcValue == undefined) {
				return null;
			}
		};
		if (fields.primaryPercentage) {
			return;
		}
		for (let i = 0; i < pType.length; i++) {
			if (fields[pType[i]]) {
				this.changePercentage(i, fields[pType[i]].value);
				return;
			}
		}
		mergeWith(this.urlListForm, fields, customizer);
	};

	changePercentage = (index, value) => {
		let currentPercentage = this.urlListForm[
			pType[index]
		].value;
		let delta = currentPercentage - value;

		let newValues = [];
		for (let i = 0; i < pType.length; i++) {
			if (i == index) {
				newValues[i] = value;
				continue;
			}
			newValues[i] = this.urlListForm[
				pType[i]
			].value;
		}

		for (let i = 0; i < pType.length; i++) {
			if (i != index) {
				let delta2 =
					this.urlListForm[pType[i]].value +
					delta;
				if (delta2 >= 0) {
					newValues[i] = delta2;
					break;
				} else {
					newValues[i] = 0;
					delta = delta2;
				}
			} else {
				newValues[i] = value;
			}
		}

		for (let i = 0; i < pType.length; i++) {
			this.urlListForm[pType[i]].value =
				newValues[i];
		}
	};

	remoteAddLink = flow(function*(link) {
		this.ui.currentLink = observable.object(link);

		this.ui.addingLink = true;

		const response = yield addLinks(this.urlList.id, [
			{
				url: link.url,
				anchor: link.anchor,
				linksOnPage: null,
				urlListId: this.urlList.id,
				site: null,
				username: null,
				password: null,
				dateAdded: null
			}
		]);

		this.ui.addingLink = false;

		if (response == null) {
			message.error("Failed to add new link", 5);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully added new link", 5);
		this.ui.addEditLinkDialogVisible = false;
		this.ui.currentLink = null;
		return;
	});

	//links is string value
	remoteBatchLinks = flow(function*(linksInText) {
		let lines = linksInText
			.replace("\r\n", "\n")
			.replace("\r", "\n")
			.split("\n");
		let links = [];
		for (let line of lines) {
			let parts = line.split("|x|");
			let link = {
				url: parts[0],
				anchor: parts[1],
				linksOnPage: parts[2],
				urlListId: this.urlList.id,
				site: null,
				username: null,
				password: null,
				dateAdded: null
			};
			links.push(link);
		}

		this.ui.addingLinks = true;

		const response = yield addLinks(this.urlList.id, links);

		this.ui.addingLinks = false;

		if (response == null) {
			message.error("Failed to add links. Unknown error", 5);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully added links", 3);
		this.ui.batchLinksDialogVisible = false;
		return;
	});

	remoteSaveLink = flow(function*(link) {
		this.ui.currentLink.url = link.url;
		this.ui.currentLink.anchor = link.anchor;

		this.ui.addingLink = true;

		const response = yield saveLink(this.urlList.id, {
			id: this.ui.currentLink.id,
			url: this.ui.currentLink.url,
			anchor: this.ui.currentLink.anchor,
			linksOnPage: this.ui.currentLink.linksOnPage,
			urlListId: this.urlList.id,
			site: this.ui.currentLink.site,
			username: this.ui.currentLink.username,
			password: this.ui.currentLink.password,
			dateAdded: this.ui.currentLink.dateAdded
		});

		this.ui.addingLink = false;

		if (response == null) {
			message.error("Failed to save the link", 5);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully saved new link", 5);
		this.ui.addEditLinkDialogVisible = false;
		this.ui.currentLink = null;
		return;
	});

	remoteSaveLinkSetKeywords = flow(function*() {
		this.ui.savingLinkSetKeywords = true;
		this.urlList = observable.object(
			getRawObjectFromFormObject(this.urlListForm)
		);
		const response = yield saveLinkSetKeywords(this.urlList.id, this.urlList);
		this.ui.savingLinkSetKeywords = false;
		if (response == null) {
			message.error("Failed to save the keywords", 5);
			return;
		}

		if (response.error) {
			notification.error({
				message: "Failed",
				description: (
					<div dangerouslySetInnerHTML={{ __html: response.error.message }} />
				),
				duration: 5
			});
			return;
		}

		message.success("Successfully save the keywords"), 3;
		return;
	});

	fetchLinks = flow(function*(urlListId) {
		this.ui.loadingLinks = true;

		const response = yield getLinks(urlListId);

		this.ui = this.initUI();

		if (response == null) {
			message.error("Failed to get the links. Unknown error", 5);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		this.campaign = observable.object(response.campaign);
		this.urlList = observable.object(response.urlList);
		this.urlListForm = observable.object(createFormObject(response.urlList));
		this.urls = observable.array(response.urls);

		registerHandler([
			{
				path: "url_list/" + urlListId,
				handler: (err, msg) => {
					if (err) {
						return;
					}
					var obj = JSON.parse(msg.body);

					if (obj.action == "addNew") {
						this.importLinks(obj.urls);
						return;
					}
					if (obj.action == "urlsDeleted") {
						this.deleteLinks(obj.urls);
						return;
					}
					if (obj.action == "urlEdited") {
						this.updateLink(obj.url);
					}
				}
			}
		]);
		message.success("Successfully load the links", 5);
		return;
	});
}

export default LinksStore;
